import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import {
  Copy,
  Check,
  Twitter,
  Linkedin,
  ExternalLink,
  Share2,
  Zap,
  Clock,
  BarChart3,
  Users,
  MessageCircle,
  Globe,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ShareableWorkflow {
  id: string;
  name: string;
  description: string;
  status: string;
  nodeCount: number;
  executions: number;
  timeSpent: number;       // horas de desarrollo
  createdBy: string;
  category?: string;       // ej: "Marketing", "Finanzas", "IA"
  tags?: string[];
}

interface WorkflowShareModalProps {
  workflow: ShareableWorkflow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const BASE_URL = 'https://zaphub.ffollowme.com';

function getPublicUrl(workflowId: string) {
  return `${BASE_URL}/w/${workflowId}`;
}

/** Estima horas ahorradas por semana según nº de ejecuciones y nodos */
function estimateTimeSaved(executions: number, nodeCount: number): number {
  // Cada ejecución ahorra ~(nodeCount * 0.1) horas de trabajo manual
  const saved = executions * nodeCount * 0.1;
  return Math.round(saved * 10) / 10;
}

/** Texto para Twitter/X */
function buildTwitterText(wf: ShareableWorkflow): string {
  const saved = estimateTimeSaved(wf.executions, wf.nodeCount);
  const url = getPublicUrl(wf.id);

  if (saved >= 10) {
    return `He automatizado "${wf.name}" con @ZapHubHQ y he ahorrado ${saved}h de trabajo manual 🚀\n\n${wf.nodeCount} nodos · ${wf.executions} ejecuciones\n\nMira cómo funciona 👇\n${url}\n\n#automatización #nocode #ZAPHUB`;
  }
  return `Acabo de crear "${wf.name}" en @ZapHubHQ — ${wf.nodeCount} nodos trabajando solos 🤖\n\nClona esta automatización gratis 👇\n${url}\n\n#automatización #workflow #ZAPHUB`;
}

/** Texto para LinkedIn */
function buildLinkedInText(wf: ShareableWorkflow): string {
  const saved = estimateTimeSaved(wf.executions, wf.nodeCount);
  const url = getPublicUrl(wf.id);

  return `Acabo de automatizar "${wf.name}" usando ZAPHUB, la plataforma de automatización empresarial con blockchain integrado.

Resultados hasta ahora:
✅ ${wf.executions} ejecuciones automáticas
⚡ ${wf.nodeCount} nodos encadenados${saved > 0 ? `\n⏱️ ${saved}h de trabajo manual ahorradas` : ''}

Si también quieres automatizar procesos en tu empresa, puedes ver y clonar este workflow aquí:
${url}

#automatización #productividad #ZAPHUB #blockchain #RPA`;
}

/** Texto para WhatsApp */
function buildWhatsAppText(wf: ShareableWorkflow): string {
  const url = getPublicUrl(wf.id);
  return `Mira este workflow que he creado en ZAPHUB: "${wf.name}" — ${wf.nodeCount} nodos, ${wf.executions} ejecuciones. Puedes clonarlo gratis aquí: ${url}`;
}

// ── Visual Card (preview de cómo se ve al compartir) ─────────────────────────

function ShareCard({ wf, isPublic }: { wf: ShareableWorkflow; isPublic: boolean }) {
  const saved = estimateTimeSaved(wf.executions, wf.nodeCount);

  return (
    <div className="relative rounded-xl overflow-hidden border border-border bg-gradient-to-br from-background via-accent/20 to-background p-5 space-y-3">
      {/* Top accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-purple-500 to-blue-500" />

      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <img src="/logo-zaphub.png" alt="ZAPHUB" className="h-8 w-8 object-contain" />
          <div>
            <p className="text-[10px] text-muted-foreground font-medium tracking-wider uppercase">ZAPHUB Workflow</p>
            <p className="font-bold text-sm leading-tight">{wf.name}</p>
          </div>
        </div>
        <Badge
          className={`text-[10px] flex-shrink-0 ${
            isPublic
              ? 'bg-green-500/10 text-green-600 border-green-500/20'
              : 'bg-muted text-muted-foreground'
          }`}
        >
          {isPublic ? <><Globe className="h-2.5 w-2.5 mr-1" />Público</> : <><Lock className="h-2.5 w-2.5 mr-1" />Privado</>}
        </Badge>
      </div>

      {/* Description */}
      {wf.description && (
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{wf.description}</p>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg bg-accent/50 p-2 text-center">
          <p className="text-lg font-bold text-primary">{wf.nodeCount}</p>
          <p className="text-[10px] text-muted-foreground">nodos</p>
        </div>
        <div className="rounded-lg bg-accent/50 p-2 text-center">
          <p className="text-lg font-bold text-green-500">{wf.executions}</p>
          <p className="text-[10px] text-muted-foreground">ejecuciones</p>
        </div>
        <div className="rounded-lg bg-accent/50 p-2 text-center">
          <p className="text-lg font-bold text-purple-500">{saved > 0 ? `${saved}h` : `${wf.timeSpent}h`}</p>
          <p className="text-[10px] text-muted-foreground">{saved > 0 ? 'ahorradas' : 'creación'}</p>
        </div>
      </div>

      {/* Author + CTA */}
      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-1.5">
          <div className="h-5 w-5 rounded-full bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center">
            <span className="text-[8px] text-white font-bold">{wf.createdBy.charAt(0).toUpperCase()}</span>
          </div>
          <span className="text-xs text-muted-foreground">por <span className="text-foreground font-medium">{wf.createdBy}</span></span>
        </div>
        <span className="text-[10px] text-primary font-medium">zaphub.ffollowme.com</span>
      </div>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────

export function WorkflowShareModal({ workflow, open, onOpenChange }: WorkflowShareModalProps) {
  const [copied, setCopied] = useState(false);
  const [isPublic, setIsPublic] = useState(true);

  if (!workflow) return null;

  const publicUrl = getPublicUrl(workflow.id);
  const twitterText = buildTwitterText(workflow);
  const linkedinText = buildLinkedInText(workflow);
  const whatsappText = buildWhatsAppText(workflow);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    toast.success('Link copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareTwitter = () => {
    if (!isPublic) {
      toast.error('Activa "Público" antes de compartir');
      return;
    }
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}`;
    window.open(url, '_blank', 'width=600,height=400');
    toast.success('Abriendo Twitter/X...');
  };

  const handleShareLinkedIn = () => {
    if (!isPublic) {
      toast.error('Activa "Público" antes de compartir');
      return;
    }
    // LinkedIn share via URL
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`;
    window.open(url, '_blank', 'width=600,height=600');
    // Copy text separately since LinkedIn doesn't accept pre-filled text
    navigator.clipboard.writeText(linkedinText);
    toast.success('LinkedIn abierto · Texto copiado — pégalo en el post');
  };

  const handleShareWhatsApp = () => {
    if (!isPublic) {
      toast.error('Activa "Público" antes de compartir');
      return;
    }
    const url = `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;
    window.open(url, '_blank');
  };

  const handleCopyTwitterText = () => {
    navigator.clipboard.writeText(twitterText);
    toast.success('Texto para Twitter copiado');
  };

  const handleCopyLinkedInText = () => {
    navigator.clipboard.writeText(linkedinText);
    toast.success('Texto para LinkedIn copiado');
  };

  const handleTogglePublic = (val: boolean) => {
    setIsPublic(val);
    toast.success(val ? 'Workflow publicado — ahora es visible para todos' : 'Workflow privado — solo tú puedes verlo');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-primary" />
            Compartir Workflow
          </DialogTitle>
          <DialogDescription>
            Comparte tu automatización y ayuda a otros a trabajar menos
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Visual card preview */}
          <ShareCard wf={workflow} isPublic={isPublic} />

          {/* Public toggle */}
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div className="flex items-center gap-2">
              {isPublic ? <Globe className="h-4 w-4 text-green-500" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
              <div>
                <Label className="text-sm font-medium cursor-pointer">
                  {isPublic ? 'Visible públicamente' : 'Solo tú puedes verlo'}
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isPublic
                    ? 'Cualquiera con el link puede ver y clonar este workflow'
                    : 'Activa esto para poder compartirlo en redes'}
                </p>
              </div>
            </div>
            <Switch checked={isPublic} onCheckedChange={handleTogglePublic} />
          </div>

          {/* Copy link */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Link público</Label>
            <div className="flex items-center gap-2 rounded-lg border border-border bg-accent/30 p-3">
              <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="flex-1 text-sm font-mono text-primary truncate">{publicUrl}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 flex-shrink-0"
                onClick={handleCopyLink}
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>

          {/* Social buttons */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Compartir en redes</Label>
            <div className="grid grid-cols-3 gap-2">
              {/* Twitter / X */}
              <div className="space-y-1">
                <Button
                  variant="outline"
                  className="w-full h-10 border-[#1DA1F2]/30 hover:bg-[#1DA1F2]/10 hover:border-[#1DA1F2] transition-colors"
                  onClick={handleShareTwitter}
                >
                  <Twitter className="h-4 w-4 mr-2 text-[#1DA1F2]" />
                  <span className="text-sm">Twitter / X</span>
                </Button>
                <button
                  className="w-full text-[10px] text-muted-foreground hover:text-foreground text-center transition-colors"
                  onClick={handleCopyTwitterText}
                >
                  Copiar texto
                </button>
              </div>

              {/* LinkedIn */}
              <div className="space-y-1">
                <Button
                  variant="outline"
                  className="w-full h-10 border-[#0077B5]/30 hover:bg-[#0077B5]/10 hover:border-[#0077B5] transition-colors"
                  onClick={handleShareLinkedIn}
                >
                  <Linkedin className="h-4 w-4 mr-2 text-[#0077B5]" />
                  <span className="text-sm">LinkedIn</span>
                </Button>
                <button
                  className="w-full text-[10px] text-muted-foreground hover:text-foreground text-center transition-colors"
                  onClick={handleCopyLinkedInText}
                >
                  Copiar texto
                </button>
              </div>

              {/* WhatsApp */}
              <div className="space-y-1">
                <Button
                  variant="outline"
                  className="w-full h-10 border-[#25D366]/30 hover:bg-[#25D366]/10 hover:border-[#25D366] transition-colors"
                  onClick={handleShareWhatsApp}
                >
                  <MessageCircle className="h-4 w-4 mr-2 text-[#25D366]" />
                  <span className="text-sm">WhatsApp</span>
                </Button>
                <button
                  className="w-full text-[10px] text-muted-foreground hover:text-foreground text-center transition-colors"
                  onClick={() => { navigator.clipboard.writeText(whatsappText); toast.success('Texto copiado'); }}
                >
                  Copiar texto
                </button>
              </div>
            </div>
          </div>

          {/* Stats row — motivacional */}
          <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
            <p className="text-xs text-muted-foreground text-center">
              Cada workflow compartido genera en promedio{' '}
              <span className="text-primary font-semibold">3-8 nuevos usuarios</span> en ZAPHUB
              {' '}— y tú apareces como autor en su panel
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
